import { useAuth } from "@/src/contexts/auth.context";
import { Post } from "@/src/models/post.model";
import { POSTS_QUERY_KEY } from "@/src/query-keys/post.keys";
import { useSendNotification } from "@/src/services/api/notification/notification.hooks";
import {
  EngagementFunctionError,
  placeSticker,
  removeSticker,
  StickerPlacement,
} from "@/src/services/api/posts/engagement-functions";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";

/** Replace every cached copy of a post (feeds, detail, groups) with `patch(post)`. */
export const patchCachedPost = (queryClient: ReturnType<typeof useQueryClient>, postId: string, patch: (p: any) => any) => {
  const walk = (node: any, depth = 0): any => {
    if (!node || typeof node !== "object" || depth > 8) return node;
    if (Array.isArray(node)) {
      let changed = false;
      const next = node.map((n) => {
        const r = walk(n, depth + 1);
        if (r !== n) changed = true;
        return r;
      });
      return changed ? next : node;
    }
    if (node.ItemId === postId && "reactionCount" in node) return patch(node);
    let changed = false;
    const next: any = {};
    for (const [k, v] of Object.entries(node)) {
      const r = walk(v, depth + 1);
      if (r !== v) changed = true;
      next[k] = r;
    }
    return changed ? next : node;
  };
  // Posts live under several caches (posts, ranked/following feeds, feed head, groups).
  queryClient.setQueriesData(
    { predicate: (q) => q.queryKey[0] === POSTS_QUERY_KEY || /post|feed|group/i.test(JSON.stringify(q.queryKey)) },
    (old: any) => walk(old),
  );
};

const withMine = (post: Post, mine: StickerPlacement | null, userId: string) => {
  const others = (post.stickers ?? []).filter((s) => s.userId !== userId);
  const stickers = mine ? [...others, mine] : others;
  return { ...post, stickers, stickerCount: stickers.length };
};

const ERROR_KEYS: Record<string, string> = {
  STICKER_REMOVE_FORBIDDEN: "ERROR_STICKER_FORBIDDEN",
  POST_NOT_FOUND: "ERROR_STICKER_POST_GONE",
};

/**
 * Sticker place/remove with optimistic cache updates. The Function call takes a
 * few seconds (it re-reads the post as the caller and rebuilds the projection),
 * so the sticker shows immediately and is rolled back if the server refuses.
 */
export const usePostStickers = (post: Post) => {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutate: sendNotification } = useSendNotification();
  const [pending, setPending] = useState<null | "place" | "remove">(null);
  const userId = user?.itemId ?? "";
  const canModerate = !!userId && (post.CreatedBy === userId || isAdmin);

  const fail = useCallback((error: unknown, snapshot: Post) => {
    patchCachedPost(queryClient, post.ItemId, () => snapshot);
    const code = error instanceof EngagementFunctionError ? error.code : "";
    Toast.show({ type: "error", text1: t(ERROR_KEYS[code] ?? "ERROR_STICKER_FAILED") });
  }, [post.ItemId, queryClient, t]);

  const place = useCallback(async (draft: Omit<StickerPlacement, "userId" | "placedAt" | "displayName" | "profileImageUrl">) => {
    if (!userId || pending) return;
    const snapshot = post;
    const displayName = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
    const profileImageUrl = user?.profileImageUrl?.startsWith("https://") ? user.profileImageUrl : "";
    const mine: StickerPlacement = { ...draft, userId, displayName, profileImageUrl, placedAt: new Date().toISOString() };
    patchCachedPost(queryClient, post.ItemId, (p) => withMine(p, mine, userId));
    setPending("place");
    try {
      const result = await placeSticker({ postId: post.ItemId, ...draft, displayName, profileImageUrl });
      patchCachedPost(queryClient, post.ItemId, (p) => ({
        ...withMine(p, result?.sticker ?? mine, userId),
        stickerCount: result?.stickerCount ?? p.stickerCount,
      }));
      const authorId = result?.postAuthorId ?? post.CreatedBy;
      if (result?.created && authorId && authorId !== userId) {
        sendNotification({
          userIds: [authorId],
          denormalizedPayload: JSON.stringify({
            type: "post_sticker",
            message: t("LABEL_NOTIFY_POST_STICKER", { firstName: user?.firstName, lastName: user?.lastName }),
            actorAvatar: user?.profileImageUrl,
            targetEntityId: post.ItemId,
          }),
          configurationName: "post-liked",
          responseKey: "post-action",
          responseValue: "sticker",
        });
      }
    } catch (error) {
      fail(error, snapshot);
    } finally {
      setPending(null);
    }
  }, [fail, pending, post, queryClient, sendNotification, t, user, userId]);

  const remove = useCallback(async (targetUserId?: string) => {
    if (!userId || pending) return;
    const target = targetUserId ?? userId;
    const snapshot = post;
    patchCachedPost(queryClient, post.ItemId, (p) => {
      const stickers = (p.stickers ?? []).filter((s: StickerPlacement) => s.userId !== target);
      return { ...p, stickers, stickerCount: stickers.length };
    });
    setPending("remove");
    try {
      const result = await removeSticker(post.ItemId, target === userId ? undefined : target);
      if (typeof result?.stickerCount === "number")
        patchCachedPost(queryClient, post.ItemId, (p) => ({ ...p, stickerCount: result.stickerCount }));
    } catch (error) {
      fail(error, snapshot);
    } finally {
      setPending(null);
    }
  }, [fail, pending, post, queryClient, userId]);

  const mine = (post.stickers ?? []).find((s) => s.userId === userId) ?? null;
  return { place, remove, pending, mine, canModerate, userId };
};
